import { useEffect, useState } from 'react';
import { PageType } from '@vgtv/api-client/lib/page';
import { Provider } from '@vgtv/api-client';

import { config } from '../../config';
import { trackPulsePageView } from '../../services/tracking/pulse';
import { cleanupUtmParams } from '../../services/tracking/helpers/cleanupUtmParams';
import { useVendor } from '../VendorPovider/VendorPovider';
import { PageTypeAdditional } from '../../types';
import { useAccessContext } from '../AccessProvider/AccessProvider';
import { hasAssetAccess } from '../../services/paywall';
import { setCurrentPageData } from '../../services/tracking/pageData';

import type {
  PageViewParams,
  PageViewTracker,
} from '../../services/tracking/pageViewTracker';
import type { PageData } from '../../services/tracking/pageData';
import type {
  TrackablePageType,
  PageEntityMap,
} from '../../services/tracking/helpers/pulsePage';
import type { SvpAssetAdditional } from '@vgtv/api-client/lib/svp_asset';

let providerTrackersPromise: Promise<PageViewTracker[]> | undefined;

/**
 * Dynamically loads trackers specific for the given provider.
 */
async function getProviderTrackers(provider: Provider) {
  if (!providerTrackersPromise) {
    // eslint-disable-next-line no-async-promise-executor
    providerTrackersPromise = new Promise(async (resolve) => {
      let promises: Promise<PageViewTracker>[] = [];

      if (provider === Provider.VGTV) {
        promises = [
          import('../../services/tracking/mbl').then((module) => module.mbl),
          import('../../services/tracking/brandmetrics').then(
            (module) => module.brandmetrics
          ),
        ];
      }

      if (provider === Provider.AB) {
        promises = [
          import('../../services/tracking/codigo').then(
            (module) => module.codigo
          ),
        ];
      }

      const trackers = await Promise.all(promises);

      resolve(trackers);
    });
  }

  return providerTrackersPromise;
}

const isCustomPage = (
  pageType: TrackablePageType,
  entity: PageEntityMap[TrackablePageType]
): entity is PageEntityMap[PageType.CUSTOM] => {
  return pageType === PageType.CUSTOM;
};

function areEntitiesEqual<T extends TrackablePageType>(
  pageType: T,
  entity: PageEntityMap[T],
  previousEntity: PageEntityMap[TrackablePageType]
) {
  if (isCustomPage(pageType, entity)) {
    if ('slug' in previousEntity) {
      return entity.slug === previousEntity.slug;
    }

    return false;
  }

  if ('id' in previousEntity) {
    return entity.id === previousEntity.id;
  }

  return false;
}

function usePageViewTracking<T extends TrackablePageType>({
  pageType,
  entity,
}: {
  pageType: T;
  entity: PageEntityMap[T] | undefined;
}) {
  const access = useAccessContext();

  const [lastTrackedEntity, setLastTrackedEntity] = useState<
    PageEntityMap[T] | null
  >(null);
  const { vendor } = useVendor();

  // since the entities are available from SSR, and then are fetched to ensure they're up to date
  // we have to compare them to avoid double tracking
  useEffect(() => {
    if (!access.isFetched) {
      return;
    }

    if (
      !entity ||
      (lastTrackedEntity &&
        areEntitiesEqual(pageType, entity, lastTrackedEntity))
    ) {
      return;
    }

    setLastTrackedEntity(entity);

    setCurrentPageData({ pageType, entity } as PageData);

    const pageViewParams = {
      pageType,
      entity,
      vendor,
      isSalesposter:
        pageType === PageTypeAdditional.ASSET
          ? !hasAssetAccess(entity as SvpAssetAdditional, access.accesses)
          : false,
    };

    trackPulsePageView({
      ...pageViewParams,
      callback: () => {
        if (config.provider === 'vgtv') {
          cleanupUtmParams();
        }
      },
    });

    getProviderTrackers(config.provider)
      .then((trackers) => {
        trackers.forEach((tracker) =>
          tracker.trackPageView(pageViewParams as PageViewParams)
        );
      })
      .catch(console.error);
  }, [entity, pageType, lastTrackedEntity, vendor, access]);
}

export { usePageViewTracking };
